import React from 'react';
import Image from 'next/legacy/image';
import { RenderNode } from '@contentful/rich-text-react-renderer';
import { Block, BLOCKS, INLINES, Inline } from '@contentful/rich-text-types';

import { usePageDataContext } from '../../context/PageData';

import { ScrollReveal } from '../ScrollReveal';
import RichText from '../RichText';
import { EmbeddedInlineEntryNode } from '../RichText/Nodes';
import CtaWidget from '../CtaWidget';

import Link from '../Link';
import { getTextColorClass } from '../../utils/colorTokenToTailwind';
import cx from 'classnames';

import { ctaWidgetEnum, DEFAULT_EMAIL_LABEL } from '../CtaWidget/shared';

import shield from '@/images/shield.svg';

export function LeadCapture() {
  const { config: { failWhaleDisclaimer } = {}, webregIncident = false } =
    usePageDataContext();

  const RICH_TEXT_OVERRIDE: RenderNode = {
    [BLOCKS.PARAGRAPH]: (_node: Block | Inline, children) => {
      return (
        <p
          className={cx(
            'ml-3 whitespace-pre-wrap break-words text-left text-xs font-normal leading-[1.25rem] tablet:text-sm tablet:leading-[1.25rem] [&:not(:last-child)]:mb-6',
            getTextColorClass('defaultText')
          )}
        >
          {children}
        </p>
      );
    },
    [INLINES.HYPERLINK]: (node, children) => (
      <Link
        href={node.data.uri}
        className="whitespace-nowrap font-normal text-black underline transition-colors duration-200 ease-in-out hover:text-navy desktop:text-black desktop:hover:text-navy"
      >
        {children}
      </Link>
    ),
    [INLINES.EMBEDDED_ENTRY]: (node, children) => (
      <EmbeddedInlineEntryNode node={node} colour="defaultText" fontWeight="p">
        {children}
      </EmbeddedInlineEntryNode>
    )
  };

  return !webregIncident ? null : (
    <ScrollReveal>
      <section
        className="block-container mt-12 bg-light-yellow px-0 py-0 tablet:mt-3 tablet:px-12"
        data-blockid="leadCapture"
      >
        <div
          className="mt-3 box-border flex w-full max-w-screen-max flex-[1_0_100%]"
          data-blockid="lead-capture-layout"
        >
          <div className="box-border w-full rounded-none tablet:rounded-[4rem] desktop:rounded-[6.5rem]">
            <div className="-m-0 box-border flex w-full flex-wrap justify-center pb-3 pl-6 pr-3 pt-6 tablet:-m-1.5 tablet:pb-12 tablet:pl-6 tablet:pr-12 tablet:pt-6 [&>*]:p-0 [&>*]:tablet:p-1.5">
              <div className="box-column flex w-full flex-col justify-center desktop:w-8/12">
                <h3
                  className="text-center text-[1.75rem] font-medium leading-[2.5rem] desktop:text-[2rem] desktop:leading-[2.25rem]"
                  data-testid="fail-whale-incident-message"
                >
                  Uh-oh! ​​We&apos;re experiencing some challenges right now.
                </h3>

                <p className="p-4 text-center text-[0.875rem] font-normal leading-[1.125rem] tablet:px-[6rem] desktop:px-16 desktop:text-[1.125rem] desktop:leading-6">
                  Please share your email address with us so we can let you know
                  when we&apos;re back up and running.
                </p>
              </div>
              <div
                className="box-column flex w-full flex-col items-center justify-center tablet:w-10/12 desktop:w-8/12"
                id="email-column"
              >
                <CtaWidget
                  id="lead-capture-email-widget"
                  module="Lead Capture"
                  designType={ctaWidgetEnum.EMAIL}
                  sysId="registrationEntrypoint"
                  errorPosition="bottom"
                  data-elementid="lead-capture-email-widget"
                  label={DEFAULT_EMAIL_LABEL}
                  placeholder={DEFAULT_EMAIL_LABEL}
                  buttonContent="Submit"
                  height="100%"
                  width="100%"
                />
                {failWhaleDisclaimer && (
                  <div className="mt-6 box-border">
                    <div className="mx-8 my-0 box-border flex flex-row items-center justify-between desktop:mx-6">
                      {/* Empty alt tag to hide decorative icons from screen readers */}
                      <Image src={shield} alt="" />
                      <RichText
                        data={failWhaleDisclaimer}
                        overrideOptions={RICH_TEXT_OVERRIDE}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </ScrollReveal>
  );
}
